/**
 * Change Form Page
 * Create or edit change requests
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  Save2, 
  Send, 
  DocumentText,
  Calendar,
  Clock,
  Warning2,
  Add,
  Trash,
  InfoCircle
} from 'iconsax-react';
import { 
  getChange, 
  createChange, 
  updateChange, 
  submitChange,
  getTeams, 
  getTemplates,
  getTemplate,
  Team,
  ChangeTemplate,
  TemplateCustomField,
  ChangeRequest,
  ChangeCategory,
  ChangeType,
  Priority,
  getChangeCategories,
  ChangeCategoryDef
} from '../../services/changeManagementApi';
import toast from 'react-hot-toast';

const ChangeFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id && id !== 'new';
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [teams, setTeams] = useState<Team[]>([]);
  const [templates, setTemplates] = useState<ChangeTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [templateCustomFields, setTemplateCustomFields] = useState<TemplateCustomField[]>([]);
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, any>>({});
  const [categories, setCategories] = useState<ChangeCategoryDef[]>([]);
  
  // Form state
  const [formData, setFormData] = useState<Partial<ChangeRequest>>({
    title: '',
    description: '',
    change_type: 'normal',
    category: 'infrastructure',
    sub_category: '',
    team: '',
    regions: [],
    priority: 'medium',
    impact: 'medium',
    urgency: 'medium',
    affected_systems: [],
    affected_services: [],
    justification: '',
    implementation_plan: '',
    rollback_plan: '',
    test_plan: '',
    communication_plan: '',
    estimated_duration: 60,
    maintenance_window: false,
    requires_downtime: false,
    downtime_duration: 0,
    external_ticket: ''
  });
  
  // Available regions
  const regionOptions = ['BOM', 'SNG', 'RUH', 'AUH', 'JNB', 'Qatar'];
  
  const [newSystem, setNewSystem] = useState('');
  const [newService, setNewService] = useState('');
  const urgencySelectRef = useRef<HTMLSelectElement>(null);

  const fetchInitialData = useCallback(async () => {
    setLoading(true);
    try {
      const [teamsData, templatesData] = await Promise.all([
        getTeams(),
        getTemplates({ active_only: true })
      ]);
      setTeams(teamsData);
      setTemplates(templatesData);
      
      if (isEdit && id) {
        const change = await getChange(id);
        // Format datetime for datetime-local inputs (YYYY-MM-DDTHH:mm)
        const formatDateTimeLocal = (dateString: string | undefined) => {
          if (!dateString) return '';
          try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return '';
            // Format as YYYY-MM-DDTHH:mm (datetime-local format)
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            return `${year}-${month}-${day}T${hours}:${minutes}`;
          } catch {
            return '';
          }
        };
        setFormData({
          ...change,
          scheduled_start: formatDateTimeLocal(change.scheduled_start),
          scheduled_end: formatDateTimeLocal(change.scheduled_end)
        });
        
        // Load custom fields if template is used
        if (change.template_id) {
          try {
            const template = await getTemplate(change.template_id);
            if (template && template.custom_fields) {
              setTemplateCustomFields(template.custom_fields);
              setCustomFieldValues(change.custom_field_values || {});
            }
          } catch (error) {
            console.error('Error loading template for edit:', error);
          }
        }
      } else if (teamsData.length > 0) {
        setFormData(prev => ({ ...prev, team: teamsData[0].id }));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load form data');
    } finally {
      setLoading(false);
    }
  }, [id, isEdit]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  useEffect(() => {
    // Load change categories for dynamic dropdowns
    const loadCategories = async () => {
      try {
        const data = await getChangeCategories(true);
        setCategories(data);
      } catch (error) {
        console.error('Error loading change categories:', error);
      }
    };
    loadCategories();
  }, []);

  const handleTemplateSelect = async (templateId: string) => {
    if (!templateId) {
      setTemplateCustomFields([]);
      setCustomFieldValues({});
      setSelectedTemplate('');
      return;
    }
    
    try {
      const template = await getTemplate(templateId);
      if (template) {
        setFormData(prev => ({
          ...prev,
          category: template.category,
          team: template.team,
          implementation_plan: template.implementation_plan || prev.implementation_plan,
          rollback_plan: template.rollback_plan || prev.rollback_plan,
          test_plan: template.test_plan || prev.test_plan,
          estimated_duration: template.estimated_duration || prev.estimated_duration,
          requires_downtime: template.requires_downtime,
          change_type: 'standard',
          is_from_template: true,
          template_id: template.id
        }));
        
        // Load custom fields from template
        const customFields = template.custom_fields || [];
        setTemplateCustomFields(customFields);
        
        // Initialize custom field values with defaults
        const initialValues: Record<string, any> = {};
        customFields.forEach(field => {
          if (field.default_value !== undefined && field.default_value !== null && field.default_value !== '') {
            initialValues[field.field_key] = field.default_value;
          }
        });
        setCustomFieldValues(initialValues);
        
        setSelectedTemplate(templateId);
        toast.success('Template applied');
      }
    } catch (error: any) {
      console.error('Error loading template:', error);
      toast.error('Failed to load template');
    }
  };

  const handleSubmit = async (submitForApproval = false) => {
    // Validation
    if (!formData.title?.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!formData.description?.trim()) {
      toast.error('Description is required');
      return;
    }
    if (!formData.team) {
      toast.error('Team is required');
      return;
    }
    if (!formData.regions || formData.regions.length === 0) {
      toast.error('At least one region must be selected');
      return;
    }
    
    if (submitForApproval) {
      if (!formData.implementation_plan?.trim()) {
        toast.error('Implementation plan is required for submission');
        return;
      }
      if (!formData.rollback_plan?.trim()) {
        toast.error('Rollback plan is required for submission');
        return;
      }
    }
    
    // Validate required custom fields
    for (const field of templateCustomFields) {
      if (field.is_required && (!customFieldValues[field.field_key] || customFieldValues[field.field_key] === '')) {
        toast.error(`${field.label} is required`);
        return;
      }
    }
    
    setSaving(true);
    try {
      const submitData = {
        ...formData,
        custom_field_values: customFieldValues,
      };
      
      let savedChange: ChangeRequest;
      
      if (isEdit) {
        savedChange = await updateChange(id!, submitData);
        toast.success('Change updated successfully');
      } else {
        savedChange = await createChange(submitData);
        toast.success('Change created successfully');
      }
      
      if (submitForApproval) {
        await submitChange(savedChange.change_id);
        toast.success('Change submitted for approval');
      }
      
      navigate(`/changes/${savedChange.change_id}`);
    } catch (error: any) {
      console.error('Error saving change:', error);
      toast.error(error.response?.data?.error || 'Failed to save change');
    } finally {
      setSaving(false);
    }
  };

  const handleAddSystem = () => {
    if (newSystem.trim() && !formData.affected_systems?.includes(newSystem.trim())) {
      setFormData(prev => ({
        ...prev,
        affected_systems: [...(prev.affected_systems || []), newSystem.trim()]
      }));
      setNewSystem('');
    }
  };

  const handleRemoveSystem = (system: string) => {
    setFormData(prev => ({
      ...prev,
      affected_systems: (prev.affected_systems || []).filter(s => s !== system)
    }));
  };

  const handleAddService = () => {
    if (newService.trim() && !formData.affected_services?.includes(newService.trim())) {
      setFormData(prev => ({
        ...prev,
        affected_services: [...(prev.affected_services || []), newService.trim()]
      }));
      setNewService('');
    }
  };

  const handleRemoveService = (service: string) => {
    setFormData(prev => ({
      ...prev,
      affected_services: (prev.affected_services || []).filter(s => s !== service)
    }));
  };

  const categoryOptions: ChangeCategory[] =
    categories.length > 0
      ? (categories.map(c => c.code) as ChangeCategory[])
      : ([
          'infrastructure',
          'rule',
          'integration',
          'security',
          'network',
          'application',
          'configuration',
          'other',
        ] as ChangeCategory[]);

  const typeOptions: ChangeType[] = ['normal', 'standard', 'emergency'];
  const priorityOptions: Priority[] = ['low', 'medium', 'high', 'critical'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-lg bg-background-card border border-border-dark hover:bg-background-hover transition-colors"
        >
          <ArrowLeft size={20} color="#9CA3AF" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-white">
            {isEdit ? 'Edit Change Request' : 'New Change Request'}
          </h1>
          <p className="text-gray-400 mt-1">
            {isEdit ? 'Update the change request details' : 'Create a new change request for approval'}
          </p>
        </div>
      </div>

      {/* Template Selection (only for new changes) */}
      {!isEdit && templates.length > 0 && (
        <div className="bg-background-card rounded-xl p-4 border border-border-dark mb-6">
          <div className="flex items-center gap-2 mb-3">
            <DocumentText size={20} color="#426CFF" />
            <span className="text-white font-medium">Use a Template (Optional)</span>
          </div>
          <select
            value={selectedTemplate}
            onChange={(e) => handleTemplateSelect(e.target.value)}
            className="w-full px-4 py-2 bg-background-hover rounded-lg border border-border-dark text-white focus:outline-none focus:border-primary"
          >
            <option value="">Select a template...</option>
            {templates.map(template => (
              <option key={template.id} value={template.id}>
                {template.template_name} ({template.category})
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="space-y-6">
        {/* Basic Information */}
        <div className="bg-background-card rounded-xl p-6 border border-border-dark">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <InfoCircle size={20} color="#426CFF" />
            Basic Information
          </h2>
          
          <div className="space-y-4">
            {/* Title */}
            <div>
              <label className="block text-sm text-gray-400 mb-1">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Brief description of the change"
                className="w-full px-4 py-2 bg-background-hover rounded-lg border border-border-dark text-white placeholder-gray-500 focus:outline-none focus:border-primary"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm text-gray-400 mb-1">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Detailed description of what will be changed and why"
                rows={4}
                className="w-full px-4 py-2 bg-background-hover rounded-lg border border-border-dark text-white placeholder-gray-500 focus:outline-none focus:border-primary resize-none"
              />
            </div>

            {/* Type, Category, Team */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Change Type</label>
                <select
                  value={formData.change_type}
                  onChange={(e) => {
                    const newValue = e.target.value as ChangeType;
                    setFormData(prev => ({ ...prev, change_type: newValue }));
                  }}
                  className="w-full px-4 py-2 bg-background-hover rounded-lg border border-border-dark text-white focus:outline-none focus:border-primary"
                >
                  {typeOptions.map(type => (
                    <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => {
                    const newValue = e.target.value as ChangeCategory;
                    setFormData(prev => ({ ...prev, category: newValue }));
                  }}
                  className="w-full px-4 py-2 bg-background-hover rounded-lg border border-border-dark text-white focus:outline-none focus:border-primary"
                >
                  {categoryOptions.map(cat => {
                    const def = categories.find(c => c.code === cat);
                    const label = def?.name || cat;
                    return (
                      <option key={cat} value={cat}>
                        {label.charAt(0).toUpperCase() + label.slice(1)}
                      </option>
                    );
                  })}
                </select>
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">
                  Team <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.team}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setFormData(prev => ({ ...prev, team: newValue }));
                  }}
                  className="w-full px-4 py-2 bg-background-hover rounded-lg border border-border-dark text-white focus:outline-none focus:border-primary"
                >
                  <option value="">Select team...</option>
                  {teams.map(team => (
                    <option key={team.id} value={team.id}>{team.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Regions - Multi-select */}
            <div>
              <label className="block text-sm text-gray-400 mb-1">
                Region(s) <span className="text-red-500">*</span>
              </label>
              <div className="space-y-2">
                <div className="flex flex-wrap gap-2">
                  {regionOptions.map(region => (
                    <label
                      key={region}
                      className="flex items-center gap-2 px-3 py-2 bg-background-hover rounded-lg border border-border-dark cursor-pointer hover:border-primary transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={formData.regions?.includes(region) || false}
                        onChange={(e) => {
                          const currentRegions = formData.regions || [];
                          if (e.target.checked) {
                            setFormData({
                              ...formData,
                              regions: [...currentRegions, region]
                            });
                          } else {
                            setFormData({
                              ...formData,
                              regions: currentRegions.filter(r => r !== region)
                            });
                          }
                        }}
                        className="w-4 h-4 text-primary bg-background-hover border-border-dark rounded focus:ring-primary focus:ring-2"
                      />
                      <span className="text-white text-sm">{region}</span>
                    </label>
                  ))}
                </div>
                {formData.regions && formData.regions.length > 0 && (
                  <p className="text-xs text-gray-500">
                    Selected: {formData.regions.join(', ')}
                  </p>
                )}
              </div>
            </div>

            {/* External Ticket */}
            <div>
              <label className="block text-sm text-gray-400 mb-1">External Ticket ID (Optional)</label>
              <input
                type="text"
                value={formData.external_ticket}
                onChange={(e) => setFormData({ ...formData, external_ticket: e.target.value })}
                placeholder="e.g., JIRA-123, INC0001234"
                className="w-full px-4 py-2 bg-background-hover rounded-lg border border-border-dark text-white placeholder-gray-500 focus:outline-none focus:border-primary"
              />
            </div>
          </div>
        </div>

        {/* Custom Fields from Template */}
        {templateCustomFields.length > 0 && (
          <div className="bg-background-card rounded-xl p-6 border border-border-dark">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <DocumentText size={20} color="#60A5FA" />
              Template Custom Fields
            </h2>
            <p className="text-sm text-gray-400 mb-4">
              Additional fields defined in the selected template
            </p>
            
            <div className="space-y-4">
              {templateCustomFields.map((field, index) => (
                <div key={index}>
                  <label className="block text-sm text-gray-400 mb-1">
                    {field.label}
                    {field.is_required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  
                  {field.description && (
                    <p className="text-xs text-gray-500 mb-1">{field.description}</p>
                  )}
                  
                  {field.type === 'text' && (
                    <input
                      type="text"
                      value={customFieldValues[field.field_key] || ''}
                      onChange={(e) => setCustomFieldValues({
                        ...customFieldValues,
                        [field.field_key]: e.target.value
                      })}
                      placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                      required={field.is_required}
                      className="w-full px-4 py-2 bg-background-hover rounded-lg border border-border-dark text-white placeholder-gray-500 focus:outline-none focus:border-primary"
                    />
                  )}
                  
                  {field.type === 'number' && (
                    <input
                      type="number"
                      value={customFieldValues[field.field_key] || ''}
                      onChange={(e) => setCustomFieldValues({
                        ...customFieldValues,
                        [field.field_key]: e.target.value ? parseFloat(e.target.value) : ''
                      })}
                      placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                      required={field.is_required}
                      className="w-full px-4 py-2 bg-background-hover rounded-lg border border-border-dark text-white placeholder-gray-500 focus:outline-none focus:border-primary"
                    />
                  )}
                  
                  {field.type === 'textarea' && (
                    <textarea
                      value={customFieldValues[field.field_key] || ''}
                      onChange={(e) => setCustomFieldValues({
                        ...customFieldValues,
                        [field.field_key]: e.target.value
                      })}
                      placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                      rows={3}
                      required={field.is_required}
                      className="w-full px-4 py-2 bg-background-hover rounded-lg border border-border-dark text-white placeholder-gray-500 focus:outline-none focus:border-primary resize-none"
                    />
                  )}
                  
                  {field.type === 'select' && (
                    <select
                      value={customFieldValues[field.field_key] || ''}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setCustomFieldValues(prev => ({
                          ...prev,
                          [field.field_key]: newValue
                        }));
                      }}
                      required={field.is_required}
                      className="w-full px-4 py-2 bg-background-hover rounded-lg border border-border-dark text-white focus:outline-none focus:border-primary"
                    >
                      <option value="">Select {field.label.toLowerCase()}...</option>
                      {field.options?.map((option, optIndex) => (
                        <option key={optIndex} value={option}>{option}</option>
                      ))}
                    </select>
                  )}
                  
                  {field.type === 'date' && (
                    <input
                      type="date"
                      value={customFieldValues[field.field_key] || ''}
                      onChange={(e) => setCustomFieldValues({
                        ...customFieldValues,
                        [field.field_key]: e.target.value
                      })}
                      required={field.is_required}
                      className="w-full px-4 py-2 bg-background-hover rounded-lg border border-border-dark text-white focus:outline-none focus:border-primary [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-100 [&::-webkit-calendar-picker-indicator]:brightness-0 [&::-webkit-calendar-picker-indicator]:invert"
                      style={{ 
                        colorScheme: 'dark',
                        WebkitAppearance: 'none',
                        MozAppearance: 'textfield'
                      }}
                    />
                  )}
                  
                  {field.type === 'boolean' && (
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id={`custom_field_${field.field_key}`}
                        checked={customFieldValues[field.field_key] || false}
                        onChange={(e) => setCustomFieldValues({
                          ...customFieldValues,
                          [field.field_key]: e.target.checked
                        })}
                        className="w-5 h-5 rounded border-border-dark bg-background-hover text-primary focus:ring-primary"
                      />
                      <label htmlFor={`custom_field_${field.field_key}`} className="text-gray-300">
                        {field.placeholder || `Enable ${field.label.toLowerCase()}`}
                      </label>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Risk Assessment */}
        <div className="bg-background-card rounded-xl p-6 border border-border-dark">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Warning2 size={20} color="#FB923C" />
            Risk Assessment
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Priority</label>
              <select
                value={formData.priority || 'medium'}
                onChange={(e) => {
                  const newValue = e.target.value as Priority;
                  setFormData(prev => ({ ...prev, priority: newValue }));
                }}
                className="w-full px-4 py-2 bg-background-hover rounded-lg border border-border-dark text-white focus:outline-none focus:border-primary cursor-pointer"
              >
                {priorityOptions.map(p => (
                  <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm text-gray-400 mb-1">Impact</label>
              <select
                value={formData.impact || 'medium'}
                onChange={(e) => {
                  const newValue = e.target.value as Priority;
                  setFormData(prev => ({ ...prev, impact: newValue }));
                }}
                className="w-full px-4 py-2 bg-background-hover rounded-lg border border-border-dark text-white focus:outline-none focus:border-primary cursor-pointer"
              >
                {priorityOptions.map(p => (
                  <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm text-gray-400 mb-1">Urgency</label>
              <select
                ref={urgencySelectRef}
                value={formData.urgency || 'medium'}
                onChange={(e) => {
                  const newValue = e.target.value as Priority;
                  setFormData(prev => ({ ...prev, urgency: newValue }));
                }}
                onMouseDown={(e) => {
                  // Ensure dropdown opens immediately
                  if (urgencySelectRef.current) {
                    urgencySelectRef.current.focus();
                  }
                }}
                className="w-full px-4 py-2 bg-background-hover rounded-lg border border-border-dark text-white focus:outline-none focus:border-primary cursor-pointer"
                tabIndex={0}
              >
                {priorityOptions.map(p => (
                  <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Justification */}
          <div className="mt-4">
            <label className="block text-sm text-gray-400 mb-1">Business Justification</label>
            <textarea
              value={formData.justification}
              onChange={(e) => setFormData({ ...formData, justification: e.target.value })}
              placeholder="Why is this change necessary? What business problem does it solve?"
              rows={3}
              className="w-full px-4 py-2 bg-background-hover rounded-lg border border-border-dark text-white placeholder-gray-500 focus:outline-none focus:border-primary resize-none"
            />
          </div>
        </div>

        {/* Affected Items */}
        <div className="bg-background-card rounded-xl p-6 border border-border-dark">
          <h2 className="text-lg font-semibold text-white mb-4">Affected Items</h2>
          
          {/* Affected Systems */}
          <div className="mb-4">
            <label className="block text-sm text-gray-400 mb-2">Affected Systems</label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={newSystem}
                onChange={(e) => setNewSystem(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddSystem())}
                placeholder="Add a system (e.g., prod-server-01)"
                className="flex-1 px-4 py-2 bg-background-hover rounded-lg border border-border-dark text-white placeholder-gray-500 focus:outline-none focus:border-primary"
              />
              <button
                type="button"
                onClick={handleAddSystem}
                className="px-4 py-2 bg-primary rounded-lg text-white hover:bg-primary/80 transition-colors"
              >
                <Add size={20} color="#FFFFFF" />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.affected_systems?.map((system, index) => (
                <span
                  key={index}
                  className="flex items-center gap-2 px-3 py-1 bg-background-hover rounded-full text-gray-300 text-sm"
                >
                  {system}
                  <button
                    type="button"
                    onClick={() => handleRemoveSystem(system)}
                    className="text-gray-500 hover:text-red-400"
                  >
                    <Trash size={14} color="#EF4444" />
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Affected Services */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Affected Services</label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={newService}
                onChange={(e) => setNewService(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddService())}
                placeholder="Add a service (e.g., API Gateway)"
                className="flex-1 px-4 py-2 bg-background-hover rounded-lg border border-border-dark text-white placeholder-gray-500 focus:outline-none focus:border-primary"
              />
              <button
                type="button"
                onClick={handleAddService}
                className="px-4 py-2 bg-primary rounded-lg text-white hover:bg-primary/80 transition-colors"
              >
                <Add size={20} color="#FFFFFF" />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.affected_services?.map((service, index) => (
                <span
                  key={index}
                  className="flex items-center gap-2 px-3 py-1 bg-background-hover rounded-full text-gray-300 text-sm"
                >
                  {service}
                  <button
                    type="button"
                    onClick={() => handleRemoveService(service)}
                    className="text-gray-500 hover:text-red-400"
                  >
                    <Trash size={14} color="#EF4444" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Implementation Details */}
        <div className="bg-background-card rounded-xl p-6 border border-border-dark">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <DocumentText size={20} color="#34D399" />
            Implementation Details
          </h2>
          
          {/* Implementation Plan */}
          <div className="mb-4">
            <label className="block text-sm text-gray-400 mb-1">
              Implementation Plan <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.implementation_plan}
              onChange={(e) => setFormData({ ...formData, implementation_plan: e.target.value })}
              placeholder="Step-by-step instructions for implementing this change"
              rows={6}
              className="w-full px-4 py-2 bg-background-hover rounded-lg border border-border-dark text-white placeholder-gray-500 focus:outline-none focus:border-primary resize-none font-mono text-sm"
            />
          </div>

          {/* Rollback Plan */}
          <div className="mb-4">
            <label className="block text-sm text-gray-400 mb-1">
              Rollback Plan <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.rollback_plan}
              onChange={(e) => setFormData({ ...formData, rollback_plan: e.target.value })}
              placeholder="Steps to revert the change if something goes wrong"
              rows={4}
              className="w-full px-4 py-2 bg-background-hover rounded-lg border border-border-dark text-white placeholder-gray-500 focus:outline-none focus:border-primary resize-none font-mono text-sm"
            />
          </div>

          {/* Test Plan */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">Test Plan</label>
            <textarea
              value={formData.test_plan}
              onChange={(e) => setFormData({ ...formData, test_plan: e.target.value })}
              placeholder="How will you verify the change was successful?"
              rows={3}
              className="w-full px-4 py-2 bg-background-hover rounded-lg border border-border-dark text-white placeholder-gray-500 focus:outline-none focus:border-primary resize-none font-mono text-sm"
            />
          </div>
        </div>

        {/* Scheduling */}
        <div className="bg-background-card rounded-xl p-6 border border-border-dark">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Calendar size={20} color="#A78BFA" />
            Scheduling
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Scheduled Start</label>
              <div className="relative">
                <input
                  type="datetime-local"
                  value={formData.scheduled_start || ''}
                  onChange={(e) => setFormData({ ...formData, scheduled_start: e.target.value })}
                  className="w-full px-4 py-2 bg-background-hover rounded-lg border border-border-dark text-white focus:outline-none focus:border-primary [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-100 [&::-webkit-calendar-picker-indicator]:brightness-0 [&::-webkit-calendar-picker-indicator]:invert"
                  style={{ 
                    colorScheme: 'dark',
                    WebkitAppearance: 'none',
                    MozAppearance: 'textfield'
                  }}
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm text-gray-400 mb-1">Scheduled End</label>
              <div className="relative">
                <input
                  type="datetime-local"
                  value={formData.scheduled_end || ''}
                  onChange={(e) => setFormData({ ...formData, scheduled_end: e.target.value })}
                  className="w-full px-4 py-2 bg-background-hover rounded-lg border border-border-dark text-white focus:outline-none focus:border-primary [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-100 [&::-webkit-calendar-picker-indicator]:brightness-0 [&::-webkit-calendar-picker-indicator]:invert"
                  style={{ 
                    colorScheme: 'dark',
                    WebkitAppearance: 'none',
                    MozAppearance: 'textfield'
                  }}
                  min={formData.scheduled_start || undefined}
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Estimated Duration (minutes)</label>
              <input
                type="number"
                value={formData.estimated_duration}
                onChange={(e) => setFormData({ ...formData, estimated_duration: parseInt(e.target.value) || 0 })}
                className="w-full px-4 py-2 bg-background-hover rounded-lg border border-border-dark text-white focus:outline-none focus:border-primary"
              />
            </div>
            
            <div className="flex items-center gap-3 pt-6">
              <input
                type="checkbox"
                id="maintenance_window"
                checked={formData.maintenance_window}
                onChange={(e) => setFormData({ ...formData, maintenance_window: e.target.checked })}
                className="w-5 h-5 rounded border-border-dark bg-background-hover text-primary focus:ring-primary"
              />
              <label htmlFor="maintenance_window" className="text-gray-300">
                Maintenance Window
              </label>
            </div>
            
            <div className="flex items-center gap-3 pt-6">
              <input
                type="checkbox"
                id="requires_downtime"
                checked={formData.requires_downtime}
                onChange={(e) => setFormData({ ...formData, requires_downtime: e.target.checked })}
                className="w-5 h-5 rounded border-border-dark bg-background-hover text-primary focus:ring-primary"
              />
              <label htmlFor="requires_downtime" className="text-gray-300">
                Requires Downtime
              </label>
            </div>
          </div>

          {formData.requires_downtime && (
            <div className="mt-4">
              <label className="block text-sm text-gray-400 mb-1">Expected Downtime (minutes)</label>
              <input
                type="number"
                value={formData.downtime_duration}
                onChange={(e) => setFormData({ ...formData, downtime_duration: parseInt(e.target.value) || 0 })}
                className="w-full md:w-1/3 px-4 py-2 bg-background-hover rounded-lg border border-border-dark text-white focus:outline-none focus:border-primary"
              />
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between bg-background-card rounded-xl p-4 border border-border-dark">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-6 py-2 rounded-lg border border-border-dark text-gray-300 hover:bg-background-hover transition-colors"
          >
            Cancel
          </button>
          
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => handleSubmit(false)}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2 rounded-lg bg-background-hover border border-border-dark text-white hover:border-primary transition-colors disabled:opacity-50"
            >
              <Save2 size={20} color="#FFFFFF" />
              <span>Save as Draft</span>
            </button>
            
            <button
              type="button"
              onClick={() => handleSubmit(true)}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2 rounded-lg bg-primary text-white hover:bg-primary/80 transition-colors disabled:opacity-50"
            >
              <Send size={20} color="#FFFFFF" />
              <span>Submit for Approval</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangeFormPage;
